<!--
	This is used to show a menu in a EndpointTableCell.

	See: headlessui-float for examples
		- https://stackblitz.com/github/ycs77/headlessui-float/tree/main/examples/example-vue?file=src%2Fpages%2Flistbox.vue

	Example Menu Items:
		const actionMenuItems = computed(() =>
		{
			let items = [];

			//Use action in handler so you can change label without having to modify function too
			items.push({ id: 1, text:'Edit', action: 'edit' })
			items.push({ id: 2, text:'Publish', action: 'publish' })
			items.push({ id: 3, text:'Track', action: 'track' })
			items.push({ id: 4, text:'Delete', action: 'delete', hidden: !(authStore.isPortalAdmin) })

			return items;
		})

	Menu Item Selection Handling:
		function onActionMenuItemSelected(p_item)
		{
			switch (p_item.action) {
				case "edit":
					//Do edity things
					break;
				case "delete":
					//Do Delete
					break;
			}
		}

	Placement Options
		[Position of whole menu relative to button]-[menu item position]
			top-start
			top
			top-end
			right-start
			right
			right-end = top right
			bottom-start
			bottom
			bottom-end = bottom left
			left-start
			left
			left-end


-->

<script setup>
import { Float } from '@headlessui-float/vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { computed } from 'vue'

const props = defineProps({
	record: { type: Object, required: false, default: null },//ID of the record in the table this menu is associated with
	items: { type: Array, required: true, default: () => [] },// Array of items (see data format defined above)
	disabled: { type: Boolean, default: false },//True to disable menu
	width: { type: String, default: 'w-31' },//Use to change width of menu
	placement: { type: String, default: 'bottom-end' }, // 'bottom-end'=bottom left, 'right-end'=top right
	altText: { type: String, default: 'Menu' }, // Alt text for menu svg icon if you want something else
});

const emit = defineEmits(['onActionMenuItemSelected'])

/**
 * Called when a user changes the selected value
 * @param p_menu_item Selected item
 */
function onSelected(p_menu_item)
{
	emit('onActionMenuItemSelected', p_menu_item)
}

const filteredItems = computed(() =>
	props.items.filter((item) =>
	{
		return !item.hidden
	})
)

</script>

<template>		<!-- Note that <Float> must contain 2 child elements,
			the first is the reference element, and the second is the floating element.
			It can be a Headless UI component or an HTML element.-->
	<Menu @update:modelValue="value => onSelected(value)" v-slot="{ open }">
		<Float
			:placement="props.placement"

			enter="transition duration-200 ease-out"
			enter-from="scale-95 opacity-0"
			enter-to="scale-100 opacity-100"
			leave="transition duration-150 ease-in"
			leave-from="scale-100 opacity-100"
			leave-to="scale-95 opacity-0"
			tailwindcss-origin-class
		>
			<MenuButton class="flex justify-center items-center px-5 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-500 text-sm rounded-md">
				<slot name="menu-icon">
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
					     class="stroke-icon-primary hover:stroke-icon-primary-hover"
					     :class="{'icon-primary-pressed': open }"
					>
						<title>{{ props.altText }}</title>
						<path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"  stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
						<path d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</slot>
			</MenuButton>

			<div v-show="filteredItems.length > 0">
				<MenuItems class="min-w-30 bg-neutral border border-neutral-subtle shadow-lg overflow-hidden focus:outline-none">
					<MenuItem as="template" v-for="item in filteredItems" :key="item.id" :value="item" :disabled="item.disabled">
						<button type="button" class="block w-full px-4 py-1.5 text-left text-sm text-placeholder hover:text-label hover:bg-primary-subtler" @click="onSelected(item)">
							{{ item.text }}
						</button>
					</MenuItem>
				</MenuItems>
			</div>

		</Float>
	</Menu>
</template>

<style scoped>

</style>